import React, { useState, useEffect, useContext } from 'react'
import Carousel from 'react-multi-carousel'
import { connect, useDispatch } from 'react-redux'
import useTranslation from 'next-translate/useTranslation'
import { ProductInterface } from 'types/Product'
import {
  StyledProductArea,
  ButtonWrapperLeft,
  ButtonWrapperRight,
  PaginationWrapper,
  Wrapper,
  PageNumber,
  ShowOrderPart,
  MobilePaginationWrapper,
} from './ProductAreaStyles'
import { Button } from '../Button'
import { Loader } from '../Loader'
import Icons from '../Icons'
import { Text } from '../Text'
import { ProductCard } from '@/components/Product/ProductCard'
import { getProducts } from '../../redux/actions'
import { StateInterface } from '@/types/State'

interface PaginationProps {
  total: number
  page: number
  onPageChange: (page: number) => void
}

const Pagination: React.VFC<PaginationProps> = ({ total, page, onPageChange }) => {
  const { t } = useTranslation('common')

  const max = 12
  const pages = Math.ceil(total / max)

  const handlePageChange = (nextPage) => {
    if (nextPage !== page && nextPage >= 1 && nextPage <= pages) {
      onPageChange(nextPage)
    }
    document.getElementById('path').scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  return (
    <>
      <PaginationWrapper controller>
        <PageNumber onClick={() => handlePageChange(1)}>
          <Icons.DoubleChevronLeft />
        </PageNumber>
        <PageNumber onClick={() => handlePageChange(page - 1)}>
          <Icons.ChevronLeft />
        </PageNumber>
        {page >= 3 && pages != 3 && (
          <PageNumber onClick={() => handlePageChange(1)}>
            <Text>1</Text>
          </PageNumber>
        )}
        {page >= 4 && (
          <PageNumber disabled>
            <Icons.ThreeDots />
          </PageNumber>
        )}
        {page == pages && pages >= 3 && (
          <PageNumber onClick={() => handlePageChange(page - 2)}>
            <Text>{page - 2}</Text>
          </PageNumber>
        )}
        {page > 1 && (
          <PageNumber onClick={() => handlePageChange(page - 1)}>
            <Text>{page - 1}</Text>
          </PageNumber>
        )}
        <PageNumber onClick={() => handlePageChange(page)} active>
          <Text>{page}</Text>
        </PageNumber>
        {pages >= page + 1 && (
          <PageNumber onClick={() => handlePageChange(page + 1)}>
            <Text>{page + 1}</Text>
          </PageNumber>
        )}
        {page == 1 && pages > 3 && (
          <PageNumber onClick={() => handlePageChange(page + 2)}>
            <Text>{page + 2}</Text>
          </PageNumber>
        )}
        {page < pages - 2 && pages > 4 && (
          <PageNumber disabled>
            <Icons.ThreeDots />
          </PageNumber>
        )}
        {page < pages - 1 && (
          <PageNumber onClick={() => handlePageChange(pages)}>
            <Text>{pages}</Text>
          </PageNumber>
        )}
        <PageNumber onClick={() => handlePageChange(page + 1)}>
          <Icons.ChevronRight />
        </PageNumber>
        <PageNumber onClick={() => handlePageChange(pages)}>
          <Icons.DoubleChevronRight />
        </PageNumber>
      </PaginationWrapper>
      <MobilePaginationWrapper>
        <PageNumber onClick={() => handlePageChange(1)}>
          <Icons.DoubleChevronLeft />
        </PageNumber>
        <PageNumber onClick={() => handlePageChange(page - 1)}>
          <Icons.ChevronLeft />
        </PageNumber>
        <PageNumber active fluid onClick={() => handlePageChange(page - 1)}>
          {t('pagination-page-of', { page, total: pages })}
        </PageNumber>
        <PageNumber onClick={() => handlePageChange(page + 1)}>
          <Icons.ChevronRight />
        </PageNumber>
        <PageNumber onClick={() => handlePageChange(pages)}>
          <Icons.DoubleChevronRight />
        </PageNumber>
      </MobilePaginationWrapper>
    </>
  )
}

export type VariantType = 'default' | 'slider'

export interface ProductAreaProps {
  state?: any
  products: ProductInterface[]
  variant?: VariantType
  sizing?: any
  brand?: number
  category?: number
  maxOnPage?: number
  total?: number
  page?: number
  controller?: boolean
  random?: boolean
  shouldFetch?: boolean
  onPageChange?: (page: number) => void
  isLoadingExternal?: boolean
}

const ProductArea: React.FC<ProductAreaProps> = ({
  state,
  products,
  variant,
  sizing,
  brand,
  category,
  maxOnPage,
  controller,
  children,
  total,
  page = 1,
  onPageChange,
  isLoadingExternal = false,
  shouldFetch = true,
  random = false,
  ...props
}) => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const onPageChangeMidleware = (page) => {
    onPageChange(page)
  }

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true)
      await dispatch(getProducts({ brand, category, page, token: null, random }))
      setIsLoading(false)
    }
    if (shouldFetch) {
      getData()
    }
  }, [])

  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 5,
    },
    desktop: {
      breakpoint: { max: 3000, min: 1450 },
      items: 4,
    },
    desktopS: {
      breakpoint: { max: 1450, min: 1050 },
      items: 3,
    },
    tablet: {
      breakpoint: { max: 1050, min: 700 },
      items: 2,
    },
    mobile: {
      breakpoint: { max: 700, min: 0 },
      items: 1,
      centerMode: true,
    },
  }

  const ButtonGroup = ({ next, previous, goToSlide, ...rest }) => {
    const {
      carouselState: { currentSlide },
    } = rest
    return (
      <>
        <ButtonWrapperLeft {...props} className={currentSlide === 0 ? 'disable' : ''} onClick={() => previous()}>
          <Button variant="verticalRad" colorVariant="primary">
            <Icons.ChevronLeft />
          </Button>
        </ButtonWrapperLeft>
        <ButtonWrapperRight {...props} onClick={() => next()}>
          <Button variant="verticalRad" colorVariant="primary">
            <Icons.ChevronRight />
          </Button>
        </ButtonWrapperRight>
      </>
    )
  }
  const CustomDot = ({ onMove, index, onClick, active }) => (
    <ShowOrderPart
      allSlides={index + 1}
      activeSlide={active}
      className={active ? 'active' : 'inactive'}
      onClick={() => onClick()}
    />
  )

  if (variant === 'slider') {
    return (
      <Carousel
        responsive={responsive}
        draggable
        infinite
        keyBoardControl
        minimumTouchDrag={80}
        showDots
        slidesToSlide={1}
        arrows={false}
        customDot={<CustomDot />}
        customButtonGroup={<ButtonGroup />}
      >
        {products.map((product, i) => {
          if (i >= maxOnPage) {
            return null
          }
          if (i >= 7) {
            return null
          }
          return <ProductCard key={i} product={product} />
        })}
      </Carousel>
    )
  }

  if (state.products.count > 0 && !isLoading && !isLoadingExternal) {
    return (
      <Wrapper id="ProductsArea">
        <StyledProductArea sizing={sizing}>
          {products.map((data) => (
            <ProductCard key={data.id} product={data} />
          ))}
        </StyledProductArea>
        <Pagination total={total} page={page} onPageChange={onPageChangeMidleware} />
      </Wrapper>
    )
  }

  if (state.products.count === 0) {
    return (
      <Wrapper>
        <Text m="5rem auto" textColor="primary" fontWeight="bold" fontSize="xl">
          {t('no-products')}
        </Text>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Loader m="auto" />
    </Wrapper>
  )
}

const mapStateToProps = (state: StateInterface, ProductAreaProps) => ({
  state,
  ProductAreaProps,
})

export default connect(mapStateToProps)(ProductArea)

